// Date helpers (§C.8 / §3.4).
//
// All date values in LeaseFormState use the yyyy-mm-dd string format.
// Helpers here use local-time Date constructors (not UTC) because lease
// dates are always parsed in local context — "2026-01-08" means the 8th
// of January in whatever timezone the browser/server is running.
//
// The `ymd()` formatter uses local-component extraction rather than
// `toISOString().slice(0, 10)`, which formats in UTC and can shift the
// date by one day for negative-offset zones (Pacific is UTC-8/-7).

/**
 * Format a `Date` as a `yyyy-mm-dd` string using LOCAL timezone components.
 * Always use this instead of `d.toISOString().slice(0, 10)` for lease dates.
 *
 * @example
 *   ymd(new Date(2026, 0, 8)) === '2026-01-08'
 */
export function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Add `n` calendar months to a `yyyy-mm-dd` date, preserving the original
 * day-of-month when it fits in the target month, or clamping to the last
 * day of the target month when it doesn't.
 *
 * Used by §9 (payment plan schedule) for next-charge-date computation.
 * §3 lease-end semantics live in `computeEndDateFromTerm` instead.
 *
 * @example
 *   addMonths('2026-01-08', 1)  === '2026-02-08'  (day fits)
 *   addMonths('2026-01-31', 1)  === '2026-02-28'  (clamp to Feb end)
 *   addMonths('2026-01-08', 12) === '2027-01-08'  (day fits in target year)
 */
export function addMonths(ymdStr: string, n: number): string {
  const d = new Date(ymdStr + 'T00:00:00');
  // Use day-0 trick: day 0 of month X+1 is the last day of month X
  const targetYear = d.getFullYear();
  const targetMonth = d.getMonth() + n; // zero-indexed; may exceed 11
  // Clamp to last day of target month
  const clamped = new Date(targetYear, targetMonth + 1, 0);
  // If the original day fits in the target month, use it; otherwise clamp.
  const originalDay = d.getDate();
  const lastDayOfTarget = clamped.getDate();
  const resultDay = Math.min(originalDay, lastDayOfTarget);
  return ymd(new Date(targetYear, targetMonth, resultDay));
}

/**
 * Compute the lease end date from a start date and lease duration in months.
 *
 * Semantics (§3.2): end date = last day of the (start_month + months − 1)-th
 * calendar month. This is the "month-end snap" formula.
 *
 * Differs from the old `addMonths(start, months) - 1 day` formula:
 *
 * | Start       | Duration | Old formula  | New formula (§3.2) |
 * |-------------|----------|--------------|--------------------|
 * | 2026-01-01  | 12       | 2026-12-31   | 2026-12-31         |
 * | 2026-01-08  | 12       | 2027-01-07   | 2026-12-31  ← diff |
 * | 2026-02-15  | 12       | 2027-02-14   | 2027-01-31  ← diff |
 *
 * For month-start leases the formulas agree. Mid-month leases snap to
 * the end of the target month (user confirmed intentional per §3.2).
 *
 * @example
 *   computeEndDateFromTerm('2026-01-01', 12) === '2026-12-31'
 *   computeEndDateFromTerm('2026-01-08', 12) === '2026-12-31'
 *   computeEndDateFromTerm('2026-02-15', 12) === '2027-01-31'
 *   computeEndDateFromTerm('2026-01-31', 1)  === '2026-01-31'
 */
export function computeEndDateFromTerm(startYmd: string, months: number): string {
  if (!Number.isInteger(months) || months < 1) {
    throw new RangeError(
      `computeEndDateFromTerm: months must be a positive integer; got ${months}`,
    );
  }
  const start = new Date(startYmd + 'T00:00:00');
  // start.month + months points to the month AFTER the last month of the lease.
  // Day 0 of that month is the last day of the lease's last month.
  const endMonthIndex = start.getMonth() + months; // 0-indexed, may exceed 11
  const end = new Date(start.getFullYear(), endMonthIndex, 0); // day 0 = last day of prev month
  return ymd(end);
}

/**
 * Compute the number of calendar months touched (inclusive) between two dates.
 *
 * This is the "months-touched" count (§3.3): it counts each partial calendar
 * month as a full month. Used when the user edits `leaseEndDate` directly —
 * the reverse of `computeEndDateFromTerm`.
 *
 * @example
 *   computeTermFromEndDate('2026-01-01', '2026-12-31') === 12
 *   computeTermFromEndDate('2026-01-08', '2026-12-15') === 12  (months-touched)
 *   computeTermFromEndDate('2026-01-08', '2027-01-31') === 13
 */
export function computeTermFromEndDate(startYmd: string, endYmd: string): number {
  const start = new Date(startYmd + 'T00:00:00');
  const end = new Date(endYmd + 'T00:00:00');
  return (
    (end.getFullYear() * 12 + end.getMonth()) -
    (start.getFullYear() * 12 + start.getMonth()) +
    1
  );
}

/**
 * Signed calendar-day difference `b − a` between two `yyyy-mm-dd` dates.
 *
 * Positive when `b` is after `a`, negative when before, 0 when the same day.
 * Uses local-noon anchors so DST transitions can't shift the whole-day count.
 *
 * @example
 *   diffDays('2026-06-01', '2026-06-01') === 0
 *   diffDays('2026-06-01', '2026-06-05') === 4
 *   diffDays('2026-06-05', '2026-06-01') === -4
 */
export function diffDays(aYmd: string, bYmd: string): number {
  const a = new Date(aYmd + 'T12:00:00');
  const b = new Date(bYmd + 'T12:00:00');
  return Math.round((b.getTime() - a.getTime()) / 86400000);
}
